import type {
  EditorToRendererMessage,
  EditorToRendererMessageType,
  RendererToEditorMessage,
  RendererToEditorMessageType,
} from "@lureo/editor-contracts";
import { filter, Subject, type Observable } from "rxjs";
import { RendererBridgeCore } from "./RendererBridgeCore";
import { RENDERER_ACCEPTS } from "./rendererAccepts";
import {
  silentBridgeLogger,
  type RendererBridgeOptions,
  type RendererBridgePort,
  type RendererMessage,
} from "./RendererBridgePort";

/** The editor's end of an in-memory link, shaped like the frontend's RendererBridge. */
export interface InProcessEditorEnd {
  send(message: EditorToRendererMessage): void;
  on<T extends RendererToEditorMessageType>(type: T): Observable<RendererMessage<T>>;
  readonly received$: Observable<RendererToEditorMessage>;
  /** Read from the renderer directly: there is no rendererLoaded on an in-memory link. */
  accepts(type: EditorToRendererMessageType): boolean;
}

export interface InProcessRendererBridge {
  readonly renderer: RendererBridgePort;
  readonly editor: InProcessEditorEnd;
}

/**
 * Both ends of the bridge in one page (stage 2): the same messages and the same rules as postMessage, without a
 * window between them. Messages cross as JSON, as they do through the iframe, so neither end holds the other's objects.
 */
export function createInProcessRendererBridge(options: RendererBridgeOptions = {}): InProcessRendererBridge {
  const core = new RendererBridgeCore(options.logger ?? silentBridgeLogger, options.contractCheck);
  const toEditor = new Subject<RendererToEditorMessage>();
  const state = { initialized: false, disposed: false };

  const renderer: RendererBridgePort = {
    accepts: RENDERER_ACCEPTS,
    received$: core.received$,
    init: () => {
      state.initialized = true;
    },
    send: (message) => {
      const json = core.serialize(message);
      if (state.disposed) return;
      toEditor.next(JSON.parse(json) as RendererToEditorMessage);
    },
    on: (type) => core.on(type),
    dispose: () => {
      state.disposed = true;
      core.complete();
      toEditor.complete();
    },
  };

  const received$ = toEditor.asObservable();
  const editor: InProcessEditorEnd = {
    send: (message) => {
      if (!state.initialized || state.disposed) return;
      core.receive(JSON.stringify(message));
    },
    on: <T extends RendererToEditorMessageType>(type: T) =>
      received$.pipe(filter((message): message is RendererMessage<T> => message.type === type)),
    received$,
    accepts: (type) => RENDERER_ACCEPTS.includes(type),
  };

  return { renderer, editor };
}
